import { Alert, Spin } from "antd";
import React, { useCallback, useEffect, useRef, useState, useContext } from "react";
import { UserContext } from "../src/javascripts/context/UserContext";
import DrayageResourceManager from "../src/javascripts/utils/DrayageResourceManager";

// Module-level singleton state — ensures only one Drayage app mounts globally
let hasMountedGlobally = false;
let isMountingGlobally = false;
let mountOwnerId = null;

// Module-level tracking to prevent duplicate event listeners across all DrayageContainer instances
let hasSetupListenersGlobally = false;
let activeComponentCount = 0;
let savedHandleDrayageMessage = null;
let savedHandleFleetToDrayage = null;

const sendToDrayage = (type, data) => {
  if (window.drayageApp && typeof window.drayageApp.receiveMessage === "function") {
    window.drayageApp.receiveMessage(type, data);
    return true;
  }

  return false;
};

// Simple async lock to ensure only one component mounts at a time
let mountLockResolve = null;
const acquireMountLock = (timeoutMs = 5000) =>
  new Promise((resolve) => {
    if (!isMountingGlobally) {
      isMountingGlobally = true;
      resolve(true);
      return;
    }
    const existingResolve = mountLockResolve;
    mountLockResolve = (shouldMount) => {
      if (existingResolve) existingResolve(shouldMount);
    };
    setTimeout(() => {
      // Timeout — force our way through
      mountLockResolve = null;
      resolve(true);
    }, timeoutMs);
  });

const releaseMountLock = () => {
  if (mountLockResolve) {
    mountLockResolve(false);
    mountLockResolve = null;
  }
};

const DrayageContainer = ({ onReady }) => {
  const containerRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const unmountRef = useRef(null);
  const ownerIdRef = useRef(Math.random());
  const { currentUser } = useContext(UserContext);

  const resourceManager = DrayageResourceManager.getInstance();

  // Bidirectional communication setup — module-level flag prevents duplicates
  useEffect(() => {
    activeComponentCount++;

    if (!hasSetupListenersGlobally) {
      hasSetupListenersGlobally = true;

      savedHandleDrayageMessage = (event) => {
        const { type, data } = event.detail;
        window.dispatchEvent(
          new CustomEvent("drayage-to-fleet", { detail: { type, data } })
        );
      };
      window.addEventListener("drayage-message", savedHandleDrayageMessage);

      savedHandleFleetToDrayage = (event) => {
        sendToDrayage(event.detail.type, event.detail.data);
      };
      window.addEventListener("fleet-to-drayage", savedHandleFleetToDrayage);
    }

    return () => {
      activeComponentCount--;
      if (activeComponentCount === 0 && hasSetupListenersGlobally) {
        if (savedHandleDrayageMessage) {
          window.removeEventListener("drayage-message", savedHandleDrayageMessage);
        }
        if (savedHandleFleetToDrayage) {
          window.removeEventListener("fleet-to-drayage", savedHandleFleetToDrayage);
        }
        hasSetupListenersGlobally = false;
        savedHandleDrayageMessage = null;
        savedHandleFleetToDrayage = null;
      }
    };
  }, []);

  const mountDrayageApp = () => {
    if (hasMountedGlobally) return;

    if (!window.mountDrayage || !containerRef.current || unmountRef.current) {
      return;
    }

    isMountingGlobally = true;
    try {
      window.drayageApp = {
        receiveMessage: null,
        dispatchMessage: (type, data) => {
          window.dispatchEvent(
            new CustomEvent("drayage-message", { detail: { type, data } })
          );
        },
      };

      const tokenData = localStorage.getItem("auth");
      const parsedTokenData = JSON.parse(tokenData);

      let rootElement = containerRef.current.querySelector("#root");
      if (!rootElement) {
        rootElement = document.createElement("div");
        rootElement.id = "root";
        rootElement.style.width = "100%";
        rootElement.style.height = "100%";
        containerRef.current.appendChild(rootElement);
      }

      const unmount = window.mountDrayage("drayage-container", {
        userData: currentUser,
        token: parsedTokenData,
      });

      unmountRef.current = unmount;
      mountOwnerId = ownerIdRef.current;
      hasMountedGlobally = true;
      isMountingGlobally = false;

      setIsLoading(false);
      if (onReady) onReady();
    } catch (err) {
      // // console.error("Error mounting Drayage app:", err);
      isMountingGlobally = false;
      if (unmountRef.current) {
        unmountRef.current();
        unmountRef.current = null;
      }
      setError("Failed to initialize Drayage application");
      setIsLoading(false);
    }
  };

  // Load and mount Drayage app
  useEffect(() => {
    let cancelled = false;

    const loadAndMount = async () => {
      if (cancelled) return;

      // Try to acquire mount lock — if another component is mounting, wait a bit
      await acquireMountLock(3000);
      if (cancelled) return;

      // Check again after getting the lock — another component may have finished
      if (hasMountedGlobally) {
        releaseMountLock();
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);

        if (!resourceManager.isReady()) {
          await resourceManager.loadResources();
          if (cancelled) return;
        }

        // Brief delay to ensure CSS/DOM are ready
        await new Promise((r) => setTimeout(r, 100));

        mountDrayageApp();
        releaseMountLock();
      } catch (error) {
        // console.error("Failed to load Drayage resources:", error);
        isMountingGlobally = false;
        releaseMountLock();
        setError(resourceManager.getError() || "Failed to load Drayage application");
        setIsLoading(false);
      }
    };

    loadAndMount();

    return () => {
      cancelled = true;

      // Only unmount if THIS component is the owner
      if (unmountRef.current && mountOwnerId === ownerIdRef.current) {
        unmountRef.current();
        unmountRef.current = null;
        hasMountedGlobally = false;
        mountOwnerId = null;
      }

      // Release lock if we were the one waiting
      releaseMountLock();

      resourceManager.detachResources();
    };
  }, [resourceManager]);

  // Retry function
  const handleRetry = async () => {
    setIsLoading(true);
    setError(null);

    if (unmountRef.current && mountOwnerId === ownerIdRef.current) {
      unmountRef.current();
      unmountRef.current = null;
    }

    hasMountedGlobally = false;
    isMountingGlobally = false;
    mountOwnerId = null;
    releaseMountLock();

    try {
      await resourceManager.clearCache();
      await resourceManager.loadResources();
      await new Promise((r) => setTimeout(r, 100));
      mountDrayageApp();
    } catch (error) {
      // console.error("Failed to retry loading resources:", error);
      setError(resourceManager.getError() || "Failed to load Drayage application");
      setIsLoading(false);
    }
  };

  // Render error state if there's an error
  if (error) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        <Alert
          message="Connection Error"
          description={
            <div>
              <p>{error}</p>
              <button
                onClick={handleRetry}
                style={{
                  padding: "8px 16px",
                  marginTop: "10px",
                  backgroundColor: "#1890ff",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                Retry Connection
              </button>
            </div>
          }
          type="error"
          showIcon
        />
      </div>
    );
  }

  // Render the container for Drayage app
  return (
    <div
      className="drayage-container-wrapper"
      style={{ width: "100%", minHeight: "600px", position: "relative" }}
    >
      {isLoading && (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            textAlign: "center",
          }}
        >
          <Spin size="large" />
          <div style={{ marginTop: "10px" }}>Loading ...</div>
        </div>
      )}
      <div
        id="drayage-container"
        ref={containerRef}
        style={{
          width: "100%",
          height: "calc(100vh - 10px)",
          border: "none",
          borderRadius: "8px",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          opacity: isLoading ? 0 : 1,
          transition: "opacity 0.3s",
        }}
      />
    </div>
  );
};

export default DrayageContainer;
